import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import {
  IMediaProxy,
  IRouterProxy,
  IToasterProxy,
  ITranscodingJobRepositoryProxy,
  JobSettingsViewModel,
  MediaTranscoderDestinationViewModel,
  SideEffectViewModel,
  TranscodingProfileViewModel,
} from "../job-settings/job-settings.view-model";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { MediaRepository } from "../../../../domain/media-repository";
import { MediaInfo } from "../../../../domain/media";
import { IMediaTranscoderRepository } from "../../../../domain/media-transcoder/media-transcoder.repository";
import {
  ETranscodingJobPriority,
  ETranscodingJobStatus,
  TranscoderJobDetails,
} from "../../../../domain/media-transcoder/transcoder-job";
import { ICommonUtilInterface } from "../../../../domain/common/common-util.interface";
import {
  EMediaTranscodingItemStatus,
  TranscodingItem,
} from "../../../../domain/media-transcoder/transcoding-item";
import { MediaTranscodingItem } from "../../../../domain/media-transcoder/media-transcoding-item";
import { NgxSmartModalService } from "ngx-smart-modal";
import { UserService } from "app/js/src/domain/user-service";

@Component({
  selector: "evrideo-transcoder-job-editor",
  templateUrl: "./job-editor.component.html",
  styleUrls: ["./job-editor.component.scss"],
})
export class MediaTranscoderJobEditorComponent implements OnInit {
  vm: JobSettingsViewModel;
  constructor(
    toastr: ToastrService,
    router: Router,
    private _route: ActivatedRoute,
    private mediaRepository: MediaRepository,
    private _mediaTranscodingRepository: IMediaTranscoderRepository,
    private _utils: ICommonUtilInterface,
    private _changeDetectorRef: ChangeDetectorRef,
    private _ngxSmartModalService: NgxSmartModalService,
    private _userService: UserService,
  ) {
    this.vm = new JobSettingsViewModel(
      "New Job",
      _userService,
      <IToasterProxy>{
        showError(msg: string) {
          toastr.error(msg, "Error", {
            closeButton: true,
            disableTimeOut: true,
            tapToDismiss: true,
          });
        },
        showInfo(msg: string) {
          toastr.info(msg);
        },
        showSuccess(msg: string) {
          toastr.success(msg);
        },
        confirm(msg: string): Promise<boolean> {
          return Promise.resolve(confirm(msg));
        },
      },
      <IRouterProxy>{
        navigateTo(path: string, isRelativeToCurrentRoute: boolean) {
          if (isRelativeToCurrentRoute) {
            router.navigate([path], { relativeTo: _route });
          } else {
            router.navigate([path]);
          }
        },
        locationBack() {
          window.history.back();
        },
      },
      <IMediaProxy>{
        getMediaInfoByMediaId(mediaId: number): Promise<MediaInfo> {
          return mediaRepository.getMediaInfo(mediaId);
        },
        getPreviewUrl(mediaId: number): Promise<string> {
          return mediaRepository.getMediaPreviewUrl(mediaId);
        },
      },
      <ITranscodingJobRepositoryProxy>{
        getProfiles(): Promise<TranscodingProfileViewModel[]> {
          return _mediaTranscodingRepository
            .getProfiles()
            .then((profiles) =>
              profiles.map(
                (p) =>
                  new TranscodingProfileViewModel(p.id, p.name, p.isClustered),
              ),
            );
        },
        getDestinations(): Promise<MediaTranscoderDestinationViewModel[]> {
          return _mediaTranscodingRepository
            .getTenantTranscodingDestinations()
            .then((transodingDestinations) =>
              transodingDestinations.map(
                (d) =>
                  new MediaTranscoderDestinationViewModel(
                    d.id,
                    d.name,
                    d.defaultSelected,
                  ),
              ),
            );
        },
        getSideEffects(): Promise<SideEffectViewModel[]> {
          return _mediaTranscodingRepository
            .getSideEffects()
            .then((sideEffects) =>
              sideEffects.map((s) => new SideEffectViewModel(s.id, s.name)),
            );
        },
        createNewTranscodingJob(job: JobSettingsViewModel): Promise<void> {
          // tslint:disable-next-line:max-line-length
          const transcodingItems = job.mediaItemsToTranscode.map(
            (i) =>
              new MediaTranscodingItem(
                _utils.generateGuid(),
                i.mediaId,
                i.videoTitle,
                i.videoId,
                i.durationMs,
                i.mediaIn,
                i.mediaOut,
                EMediaTranscodingItemStatus.Pending,
                "",
                null,
                "",
              ),
          );
          // tslint:disable-next-line:max-line-length
          const newJob = new TranscoderJobDetails(
            job.jobId,
            job.jobName,
            job.selectedTranscodingDestination,
            job.selectedPriorityLevelId,
            ETranscodingJobStatus.Active,
            null,
            null,
            null,
            null,
            transcodingItems,
            job.selectedTranscodingProfile,
            job.selectedTranscodingSideEffect,
            job.executeOnlySideEffects,
          );
          return _mediaTranscodingRepository.createNewJob(newJob);
        },
        abortTranscodingJob(jobId: string): Promise<void> {
          return _mediaTranscodingRepository.abortJob(jobId);
        },
        changeJobPriority(
          jobId: string,
          newPriority: ETranscodingJobPriority,
        ): Promise<void> {
          return _mediaTranscodingRepository.changeJobPriority(
            jobId,
            newPriority,
          );
        },
      },
      _ngxSmartModalService,
      mediaRepository,
      _changeDetectorRef,
    );
  }

  ngOnInit() {
    this._route.params.subscribe(async (params: Params) => {
      let job = null;
      if (!!params["replicateFromId"]) {
        const replicateFromIdParam = params["replicateFromId"];
        const replicateFromJob =
          await this._mediaTranscodingRepository.getTranscodingJobDetails(
            replicateFromIdParam,
          );
        job = replicateFromJob.clone(this._utils);
        console.log("media-transcoder->New->ngOnInit->replicateFromJob", job);
      } else if (!!params["id"]) {
        const editJobId = params["id"];
        this.vm.headerTitle = "Edit Job";
        job =
          await this._mediaTranscodingRepository.getTranscodingJobDetails(
            editJobId,
          );
        this.vm.headerTitle = "Edit Job " + (job.name ?? job.id);
        this.vm.editMode = true;
      } else {
        job = await this.initNewJob();
      }
      await this.vm.initFields();
      this._changeDetectorRef.detectChanges();
      this.vm.lodJobDetails(job);
      console.log("media-transcoder-> changeDetectorRef.detectChanges");
      this._changeDetectorRef.detectChanges();
    });
  }

  private async initNewJob() {
    const uid = this._utils.generateGuid();
    const allProfiles = await this._mediaTranscodingRepository.getProfiles();
    const allDestinations =
      await this._mediaTranscodingRepository.getTenantTranscodingDestinations();
    const selectedDestination =
      allDestinations.find((d) => d.defaultSelected) || allDestinations[0];
    const newJob = new TranscoderJobDetails(
      uid,
      "",
      selectedDestination.id,
      ETranscodingJobPriority.Low,
      ETranscodingJobStatus.Active,
      null,
      null,
      null,
      null,

      [],
      allProfiles[0].id,
    );
    return newJob;
  }
}
